import { useState, useEffect } from "react";
import { useToast } from "@/components/base/Toast";

export interface CustomField {
  id: string;
  label: string;
  type: string;
  required: boolean;
  showInList: boolean;
  active: boolean;
  options?: string[];
  category: "lead" | "company";
}

const defaultLeadFields: CustomField[] = [
  { id: "tmp-l1", label: "Property Type", type: "Dropdown", required: true, showInList: true, active: true, options: ["2BHK", "3BHK", "Villa", "Plot", "Commercial"], category: "lead" },
  { id: "tmp-l2", label: "Budget Range", type: "Dropdown", required: true, showInList: true, active: true, options: ["Below 30L", "30-50L", "50L-1Cr", "Above 1Cr"], category: "lead" },
  { id: "tmp-l3", label: "Preferred Location", type: "Text Input", required: false, showInList: true, active: true, category: "lead" },
  { id: "tmp-l4", label: "Loan Required", type: "Toggle", required: false, showInList: false, active: true, category: "lead" },
  { id: "tmp-l5", label: "Site Visit Date", type: "Date", required: false, showInList: false, active: true, category: "lead" },
];

const defaultCompanyFields: CustomField[] = [
  { id: "tmp-c1", label: "Company Legal Name", type: "Text Input", required: true, showInList: true, active: true, category: "company" },
  { id: "tmp-c2", label: "GST Number", type: "Text Input", required: false, showInList: false, active: true, category: "company" },
  { id: "tmp-c3", label: "PAN Number", type: "Text Input", required: false, showInList: false, active: true, category: "company" },
];

const fieldTypeOptions = ["Text Input", "Number", "Dropdown", "Multi-select", "Date", "Toggle", "Phone", "Email", "Textarea", "Currency"];

const industryFieldExamples = [
  { id: "real-estate", name: "Real Estate", fields: [{ name: "Property Type", type: "Dropdown" }, { name: "Budget Range", type: "Dropdown" }, { name: "Preferred Location", type: "Text Input" }, { name: "Loan Required", type: "Toggle" }, { name: "Site Visit Date", type: "Date" }] },
  { id: "manufacturing", name: "Manufacturing", fields: [{ name: "Product Category", type: "Dropdown" }, { name: "Order Quantity", type: "Number" }, { name: "Delivery Timeline", type: "Dropdown" }, { name: "Payment Terms", type: "Dropdown" }, { name: "Sample Required", type: "Toggle" }] },
  { id: "education", name: "Education", fields: [{ name: "Course Interested", type: "Dropdown" }, { name: "Batch Year", type: "Dropdown" }, { name: "Scholarship Required", type: "Toggle" }, { name: "Previous Score", type: "Number" }, { name: "Parent Contact", type: "Phone" }] },
  { id: "healthcare", name: "Healthcare", fields: [{ name: "Treatment Type", type: "Dropdown" }, { name: "Insurance Provider", type: "Text Input" }, { name: "Appointment Date", type: "Date" }, { name: "Medical History", type: "Text Input" }] },
  { id: "saas", name: "SaaS / IT", fields: [{ name: "Company Size", type: "Dropdown" }, { name: "Current Tool", type: "Text Input" }, { name: "Budget/Month", type: "Number" }, { name: "Trial Required", type: "Toggle" }, { name: "Decision Timeline", type: "Dropdown" }] },
  { id: "retail", name: "Retail & Distribution", fields: [{ name: "Product Category", type: "Dropdown" }, { name: "Order Value", type: "Number" }, { name: "Payment Mode", type: "Dropdown" }, { name: "GST Number", type: "Text Input" }] },
];

const tempId = () => `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

type Props = {
  leadFields: CustomField[];
  companyFields: CustomField[];
  onFieldsChange: (leadFields: CustomField[], companyFields: CustomField[]) => void;
  onSave: (lead: CustomField[], company: CustomField[]) => Promise<void>;
};

export default function CustomFieldsBuilder({
  leadFields: propLead,
  companyFields: propCompany,
  onFieldsChange,
  onSave,
}: Props) {
  const { showToast } = useToast();

  const [leadFields, setLeadFields] = useState<CustomField[]>(propLead.length > 0 ? propLead : defaultLeadFields);
  const [companyFields, setCompanyFields] = useState<CustomField[]>(propCompany.length > 0 ? propCompany : defaultCompanyFields);
  const [activeTab, setActiveTab] = useState<"lead" | "company">("lead");

  // Add / Edit Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingField, setEditingField] = useState<CustomField | null>(null);
  const [modalLabel, setModalLabel] = useState("");
  const [modalType, setModalType] = useState("Text Input");
  const [modalRequired, setModalRequired] = useState(false);
  const [modalShowInList, setModalShowInList] = useState(false);
  const [modalActive, setModalActive] = useState(true);
  const [modalOptions, setModalOptions] = useState<string[]>([]);
  const [newOptionInput, setNewOptionInput] = useState("");

  const [showExamples, setShowExamples] = useState(false);
  const [confirmExample, setConfirmExample] = useState<(typeof industryFieldExamples)[0] | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Sync with props when loaded from API
  useEffect(() => {
    setLeadFields(propLead);
  }, [propLead]);

  useEffect(() => {
    setCompanyFields(propCompany);
  }, [propCompany]);

  const updateAll = (l: CustomField[], c: CustomField[]) => {
    setLeadFields(l);
    setCompanyFields(c);
    onFieldsChange(l, c);
  };

  const currentFields = activeTab === "lead" ? leadFields : companyFields;

  const handleToggle = (id: string, key: "required" | "showInList" | "active") => {
    if (activeTab === "lead") {
      const updated = leadFields.map((f) => (f.id === id ? { ...f, [key]: !f[key] } : f));
      updateAll(updated, companyFields);
    } else {
      const updated = companyFields.map((f) => (f.id === id ? { ...f, [key]: !f[key] } : f));
      updateAll(leadFields, updated);
    }
  };

  const handleDelete = (id: string) => {
    if (activeTab === "lead") {
      const updated = leadFields.filter((f) => f.id !== id);
      updateAll(updated, companyFields);
    } else {
      const updated = companyFields.filter((f) => f.id !== id);
      updateAll(leadFields, updated);
    }
    showToast("Field removed", "info");
  };

  // Open modal for Adding new field
  const handleOpenAddModal = () => {
    setEditingField(null);
    setModalLabel("");
    setModalType("Text Input");
    setModalRequired(false);
    setModalShowInList(false);
    setModalActive(true);
    setModalOptions([]);
    setNewOptionInput("");
    setShowModal(true);
  };
console.log("confirmExample==>" ,confirmExample ,currentFields)
  // Open modal for Editing existing field
  const handleOpenEditModal = (field: CustomField) => {
    setEditingField(field);
    setModalLabel(field.label);
    setModalType(field.type);
    setModalRequired(field.required);
    setModalShowInList(field.showInList);
    setModalActive(field.active);
    setModalOptions(field.options ? [...field.options] : []);
    setNewOptionInput("");
    setShowModal(true);
  };

  const handleAddOption = () => {
    if (newOptionInput.trim()) {
      setModalOptions((prev) => [...prev, newOptionInput.trim()]);
      setNewOptionInput("");
    }
  };

  const handleRemoveOption = (index: number) => {
    setModalOptions((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSaveModalField = () => {
    if (!modalLabel.trim()) {
      showToast("Please enter a field label", "error");
      return;
    }

    if (editingField) {
      // Update existing field
      const updatedItem: CustomField = {
        ...editingField,
        label: modalLabel.trim(),
        type: modalType,
        required: modalRequired,
        showInList: modalShowInList,
        active: modalActive,
        options: (modalType === "Dropdown" || modalType === "Multi-select") ? [...modalOptions] : (modalOptions.length > 0 ? modalOptions : undefined),
      };

      if (activeTab === "lead") {
        const updated = leadFields.map((f) => (f.id === editingField.id ? updatedItem : f));
        updateAll(updated, companyFields);
      } else {
        const updated = companyFields.map((f) => (f.id === editingField.id ? updatedItem : f));
        updateAll(leadFields, updated);
      }
      showToast(`Field "${updatedItem.label}" updated`, "success");
    } else {
      // Add new field
      const newFieldItem: CustomField = {
        id: tempId(),
        label: modalLabel.trim(),
        type: modalType,
        required: modalRequired,
        showInList: modalShowInList,
        active: modalActive,
        options: (modalType === "Dropdown" || modalType === "Multi-select") ? [...modalOptions] : (modalOptions.length > 0 ? modalOptions : undefined),
        category: activeTab,
      };

      if (activeTab === "lead") {
        updateAll([...leadFields, newFieldItem], companyFields);
      } else {
        updateAll(leadFields, [...companyFields, newFieldItem]);
      }
      showToast(`Field "${newFieldItem.label}" added`, "success");
    }

    setShowModal(false);
  };

  const confirmUseExample = () => {
    if (!confirmExample) return;
    const newFields: CustomField[] = confirmExample.fields.map((f) => ({
      id: tempId(),
      label: f.name,
      type: f.type,
      required: false,
      showInList: true,
      active: true,
      category: activeTab,
      options: f.type === "Dropdown" ? ["Option A", "Option B", "Option C"] : undefined,
    }));

    if (activeTab === "lead") {
      updateAll(newFields, companyFields);
    } else {
      updateAll(leadFields, newFields);
    }

    setConfirmExample(null);
    setShowExamples(false);
    showToast(`Pre-filled ${activeTab} fields with ${confirmExample.name} examples`, "success");
  };

  const handleSaveAll = async () => {
    setIsSaving(true);
    try {
      await onSave(leadFields, companyFields);
    } catch {
      // parent handles toast
    } finally {
      setIsSaving(false);
    }
  };

  const needsOptions = modalType === "Dropdown" || modalType === "Multi-select";

  const Toggle = ({ checked, onChange }: { checked: boolean; onChange: () => void }) => (
    <button
      type="button"
      onClick={onChange}
      className={`relative w-9 h-5 rounded-full transition-colors duration-200 cursor-pointer flex-shrink-0 ${
        checked ? "bg-indigo-600" : "bg-gray-300"
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
          checked ? "translate-x-4" : "translate-x-0"
        }`}
      />
    </button>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Custom Fields Builder</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Configure custom fields for Leads and Company profiles across your CRM
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => setShowExamples(!showExamples)}
            className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors cursor-pointer"
          >
            {showExamples ? "Hide Examples" : "Industry Examples"}
          </button>
          <button
            type="button"
            onClick={handleOpenAddModal}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold transition-colors whitespace-nowrap cursor-pointer shadow-sm"
          >
            <i className="ri-add-line text-sm" /> Add Custom Field
          </button>
        </div>
      </div>

      {/* Industry Examples Accordion */}
      {showExamples && (
        <div className="bg-indigo-50/60 rounded-xl border border-indigo-100 p-4 mb-4 animate-scale-in">
          <div className="mb-3">
            <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider">
              Common Fields by Industry Presets
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Click any industry preset below to pre-fill your {activeTab === "lead" ? "Lead" : "Company"} custom fields.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {industryFieldExamples.map((example) => (
              <div
                key={example.id}
                className="bg-white rounded-lg border border-gray-200 p-3 hover:border-indigo-300 transition-all shadow-xs"
              >
                <p className="text-xs font-bold text-gray-900 mb-1.5">{example.name}</p>
                <div className="space-y-1 mb-3">
                  {example.fields.map((f, idx) => (
                    <div key={idx} className="flex items-center justify-between text-[11px] text-gray-600">
                      <span className="truncate pr-1">&bull; {f.name}</span>
                      <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.2 rounded flex-shrink-0">
                        {f.type}
                      </span>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setConfirmExample(example)}
                  className="w-full py-1.5 rounded-md bg-indigo-50 hover:bg-indigo-600 text-indigo-700 hover:text-white text-xs font-semibold transition-colors cursor-pointer text-center"
                >
                  Use These Fields
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-4">
        {(["lead", "company"] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-xs font-semibold transition-colors border-b-2 cursor-pointer ${
              activeTab === tab
                ? "text-indigo-600 border-indigo-600 font-bold"
                : "text-gray-500 border-transparent hover:text-gray-700"
            }`}
          >
            {tab === "lead" ? `Lead Fields (${leadFields.length})` : `Company Fields (${companyFields.length})`}
          </button>
        ))}
      </div>

      {/* Table Header */}
      <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-gray-50/80 rounded-lg text-[11px] font-semibold uppercase text-gray-500 tracking-wider mb-1">
        <div className="col-span-4">Field Label &amp; Options</div>
        <div className="col-span-2">Data Type</div>
        <div className="col-span-2 text-center">Required</div>
        <div className="col-span-2 text-center">Show in List</div>
        <div className="col-span-1 text-center">Active</div>
        <div className="col-span-1 text-right pr-2">Actions</div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-gray-100">
        {currentFields.map((field) => (
          <div
            key={field.id}
            className={`grid grid-cols-12 gap-2 px-3 py-2.5 items-center transition-colors hover:bg-gray-50/60 ${
              !field.active ? "opacity-50" : ""
            }`}
          >
            <div className="col-span-4 flex flex-col min-w-0 pr-2">
              <span className="text-sm font-semibold text-gray-900 truncate">{field.label}</span>
              {field.options && field.options.length > 0 && (
                <span className="text-[11px] text-gray-500 truncate">
                  Options: {field.options.join(", ")}
                </span>
              )}
            </div>

            <div className="col-span-2">
              <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded font-medium whitespace-nowrap">
                {field.type}
              </span>
            </div>

            <div className="col-span-2 flex justify-center">
              <Toggle checked={field.required} onChange={() => handleToggle(field.id, "required")} />
            </div>

            <div className="col-span-2 flex justify-center">
              <Toggle checked={field.showInList} onChange={() => handleToggle(field.id, "showInList")} />
            </div>

            <div className="col-span-1 flex justify-center">
              <Toggle checked={field.active} onChange={() => handleToggle(field.id, "active")} />
            </div>

            <div className="col-span-1 flex items-center justify-end gap-1">
              <button
                type="button"
                onClick={() => handleOpenEditModal(field)}
                className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors cursor-pointer"
                title="Edit Field"
              >
                <i className="ri-edit-line text-sm" />
              </button>

              <button
                type="button"
                onClick={() => handleDelete(field.id)}
                className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                title="Delete Field"
              >
                <i className="ri-delete-bin-line text-sm" />
              </button>
            </div>
          </div>
        ))}

        {currentFields.length === 0 && (
          <div className="py-10 text-center text-xs text-gray-400">
            <i className="ri-input-field text-2xl block mb-1 text-gray-300" />
            No {activeTab} custom fields defined. Click &quot;+ Add Custom Field&quot; or use industry presets.
          </div>
        )}
      </div>

      {/* Save Button Bar */}
      <div className="mt-5 pt-4 border-t border-gray-100 flex justify-end">
        <button
          type="button"
          onClick={handleSaveAll}
          disabled={isSaving}
          className="px-6 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-semibold transition-all cursor-pointer flex items-center justify-center gap-2 shadow-sm hover:shadow"
        >
          {isSaving ? (
            <>
              <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Saving Custom Fields...
            </>
          ) : (
            "Save Custom Fields"
          )}
        </button>
      </div>

      {/* Add / Edit Field Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-900">
                {editingField ? `Edit Field: ${editingField.label}` : `Add ${activeTab === "lead" ? "Lead" : "Company"} Custom Field`}
              </h3>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                  Field Label <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={modalLabel}
                  onChange={(e) => setModalLabel(e.target.value)}
                  placeholder="e.g. Preferred Location, GST Number"
                  className="w-full px-3.5 py-2 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                  Field Data Type
                </label>
                <select
                  value={modalType}
                  onChange={(e) => setModalType(e.target.value)}
                  className="w-full px-3.5 py-2 rounded-lg border border-gray-200 text-sm text-gray-900 bg-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
                >
                  {fieldTypeOptions.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              {needsOptions && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                    Dropdown / Select Options
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={newOptionInput}
                      onChange={(e) => setNewOptionInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddOption();
                        }
                      }}
                      placeholder="Add an option & press enter..."
                      className="flex-1 px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-900 focus:outline-none focus:border-indigo-400"
                    />
                    <button
                      type="button"
                      onClick={handleAddOption}
                      className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold transition-colors cursor-pointer"
                    >
                      Add
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                    {modalOptions.map((opt, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-medium"
                      >
                        {opt}
                        <button
                          type="button"
                          onClick={() => handleRemoveOption(i)}
                          className="text-indigo-400 hover:text-red-600 transition-colors cursor-pointer"
                        >
                          <i className="ri-close-line text-xs" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between py-1 border-t border-gray-100">
                <span className="text-xs font-medium text-gray-700">Required field</span>
                <Toggle checked={modalRequired} onChange={() => setModalRequired(!modalRequired)} />
              </div>

              <div className="flex items-center justify-between py-1 border-t border-gray-100">
                <span className="text-xs font-medium text-gray-700">Show in leads list column</span>
                <Toggle checked={modalShowInList} onChange={() => setModalShowInList(!modalShowInList)} />
              </div>

              <div className="flex items-center justify-between py-1 border-t border-gray-100">
                <span className="text-xs font-medium text-gray-700">Active status</span>
                <Toggle checked={modalActive} onChange={() => setModalActive(!modalActive)} />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2.5 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveModalField}
                className="px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold transition-colors cursor-pointer shadow-sm"
              >
                {editingField ? "Update Field" : "Add Field"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm example modal */}
      {confirmExample && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-sm p-6 text-center animate-scale-in">
            <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-list-check-2 text-2xl text-indigo-600" />
            </div>
            <h3 className="text-base font-bold text-gray-900">
              Replace {activeTab} fields with {confirmExample.name} preset?
            </h3>
            <p className="text-xs text-gray-500 mt-2 mb-6">
              This will populate your {activeTab} custom fields with standard {confirmExample.name} fields. You can still add, edit, or customize them after.
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setConfirmExample(null)}
                className="flex-1 py-2 rounded-lg border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmUseExample}
                className="flex-1 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold transition-colors cursor-pointer"
              >
                Confirm &amp; Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}